import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeviceFrame } from '../shell/DeviceFrame';
import { StaticListingPreview } from './StaticListingPreview';
import { NudgeAnnotationCard } from './NudgeAnnotationCard';
import { Button } from '../design-system/Button';
import { Text } from '../design-system/Text';
import { usePrototypeStore } from '../../stores/prototypeStore';
import { analytics } from '../../lib/analytics';
import { ArrowLeft } from 'lucide-react';

const TABS = [
  { key: 'a-vs-b', label: 'A vs B', left: 'A', right: 'B' },
  { key: 'b-vs-c', label: 'B vs C', left: 'B', right: 'C' },
  { key: 'recommended-vs-all', label: 'Recommended vs All', left: 'recommended', right: 'all' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const annotations: Record<string, Parameters<typeof NudgeAnnotationCard>[0]['annotation']> = {
  A: {
    intent: 'Surface FASTag status in the vehicle header so users notice it before scanning challans.',
    why: 'The header is high-visibility but does not interrupt the payment task.',
    pros: ['Always visible', 'Subtle', 'No extra tap'],
    cons: ['Easy to ignore', 'Small clickable area'],
    expected: 'Users with low balance notice the chip and may tap through when ready.',
    risk: 'Very low; it sits outside the payment path.',
    experiment: 'A/B: show chip vs hide chip. Track recharge CTR and challan pay-through rate.',
    verdict: 'accepted',
  },
  B: {
    intent: 'Insert a contextual FASTag card between challans when balance is low.',
    why: 'Contextual to the vehicle and balances urgency with relevance.',
    pros: ['Relevant', 'Dismissable', 'Educational'],
    cons: ['Adds vertical length', 'May compete with challan cards'],
    expected: 'Curious users read and tap Recharge; others scroll past.',
    risk: 'Low if placed below first 1–2 challans.',
    experiment: 'Test card position (1st, 2nd, last) and copy variants.',
    verdict: 'accepted',
  },
  C: {
    intent: 'Use a sticky banner for urgent negative or recently-declined balances.',
    why: 'Urgency demands stronger treatment, but it stays below the fold until users scroll.',
    pros: ['Hard to miss', 'Clear CTA', 'Scoped to urgent states'],
    cons: ['Can feel alarming', 'Takes bottom-screen space'],
    expected: 'Users with critical balance act immediately; others never see it.',
    risk: 'Medium if shown too often; gate to negative/declined only.',
    experiment: 'Show only on negative balance vs recent decline; measure dismiss rate.',
    verdict: 'accepted',
  },
  D: {
    intent: 'Fill whitespace below a short challan list with a useful FASTag prompt.',
    why: 'When the list is short, users may think the screen is finished; whitespace card adds value.',
    pros: ['Feels native', 'Contextual', 'No scroll friction'],
    cons: ['Only works for short lists', 'May feel like upsell'],
    expected: 'Users with few challans notice and consider recharging.',
    risk: 'Low; only appears when space exists.',
    experiment: 'Compare short-list conversion with and without whitespace card.',
    verdict: 'accepted',
  },
  E: {
    intent: 'Place a FASTag nudge immediately next to Pay Now.',
    why: 'Maximizes visibility at the highest-intent moment.',
    pros: ['Maximum visibility', 'Targets ready-to-pay users'],
    cons: ['Can be mistaken for prerequisite', 'Adds cognitive load at conversion point'],
    expected: 'Some users may abandon Pay Now to recharge first.',
    risk: 'High risk to challan conversion.',
    experiment: 'Re-run as isolated experiment with strict guardrail on Pay Now CTR.',
    verdict: 'rejected',
  },
  F: {
    intent: 'Show a celebratory post-payment FASTag card after challan payment succeeds.',
    why: 'Challan conversion is already complete; the prompt feels helpful, not blocking.',
    pros: ['No conversion risk', 'Positive timing', 'Can bundle with receipt'],
    cons: ['May feel like upsell after payment'],
    expected: 'Users in a completion mindset are more receptive to a secondary task.',
    risk: 'Very low.',
    experiment: 'A/B post-payment card vs no card for qualified users.',
    verdict: 'accepted',
  },
  recommended: {
    intent: 'Layer header chip + inline card + urgent sticky banner + post-payment card.',
    why: 'Combines subtle, contextual, and urgent signals without overloading any single placement.',
    pros: ['Full funnel coverage', 'Adaptive by qualification', 'Clear default'],
    cons: ['More components to build', 'Requires qualification gating'],
    expected: 'Qualified users see escalating relevance; unqualified users see no nudges.',
    risk: 'Low with proper gating and dismissal.',
    experiment: 'Launch as default; compare against control (no nudges) with challan CVR guardrail.',
    verdict: 'default',
  },
};

export function VariantComparisonScreen() {
  const navigate = useNavigate();
  const deviceFrame = usePrototypeStore((s) => s.deviceFrame);
  const theme = usePrototypeStore((s) => s.theme);
  const [activeTab, setActiveTab] = useState<TabKey>('a-vs-b');

  const goToVariant = (variant: string, qualification: 'low' | 'negative') => {
    analytics.emit('compare_variant_selected', { variant });
    navigate(`/challan-listing?variant=${variant}&state=${qualification}&list=long`);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-6 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div>
            <Text variant="display-medium">Variant Comparison</Text>
            <Text variant="body" color="secondary">
              Side-by-side nudge placement review.
            </Text>
          </div>
        </header>

        <div className="mb-6 flex gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-park-500 ${
                activeTab === tab.key
                  ? 'border-park-500 bg-park-50 text-park-700 dark:bg-park-900/30 dark:text-park-300'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'recommended-vs-all' ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <Text variant="title">Recommended default</Text>
              <DeviceFrame device={deviceFrame} theme={theme} className="mx-0">
                <StaticListingPreview variant="recommended" qualification="negative" />
              </DeviceFrame>
              <NudgeAnnotationCard title="Recommended" annotation={annotations.recommended} />
              <Button onClick={() => goToVariant('recommended', 'negative')}>Open recommended</Button>
            </div>
            <div className="space-y-4">
              <Text variant="title">All alternatives</Text>
              <div className="grid grid-cols-2 gap-3">
                {(['A', 'B', 'C', 'D', 'E', 'F'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => goToVariant(v, v === 'C' ? 'negative' : 'low')}
                    className="rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:border-park-300 dark:border-slate-700 dark:bg-slate-800"
                  >
                    <Text variant="label">Variant {v}</Text>
                    <Text variant="caption" color="secondary">
                      {annotations[v].verdict === 'rejected' ? 'Rejected' : 'Accepted'}
                    </Text>
                  </button>
                ))}
              </div>
              <NudgeAnnotationCard title="E — Pay Now adjacency" annotation={annotations.E} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ComparisonPair
              deviceFrame={deviceFrame}
              theme={theme}
              label={activeTab === 'a-vs-b' ? 'A — Header chip' : 'B — Inline card'}
              variant={activeTab === 'a-vs-b' ? 'A' : 'B'}
              qualification={activeTab === 'a-vs-b' ? 'low' : 'low'}
              annotation={annotations[activeTab === 'a-vs-b' ? 'A' : 'B']}
              onOpen={() => goToVariant(activeTab === 'a-vs-b' ? 'A' : 'B', 'low')}
            />
            <ComparisonPair
              deviceFrame={deviceFrame}
              theme={theme}
              label={activeTab === 'a-vs-b' ? 'B — Inline card' : 'C — Sticky banner'}
              variant={activeTab === 'a-vs-b' ? 'B' : 'C'}
              qualification={activeTab === 'a-vs-b' ? 'low' : 'negative'}
              annotation={annotations[activeTab === 'a-vs-b' ? 'B' : 'C']}
              onOpen={() => goToVariant(activeTab === 'a-vs-b' ? 'B' : 'C', activeTab === 'a-vs-b' ? 'low' : 'negative')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ComparisonPair({
  deviceFrame,
  theme,
  label,
  variant,
  qualification,
  annotation,
  onOpen,
}: {
  deviceFrame: 'android' | 'iphone';
  theme: 'light' | 'dark';
  label: string;
  variant: 'A' | 'B' | 'C';
  qualification: 'low' | 'negative';
  annotation: Parameters<typeof NudgeAnnotationCard>[0]['annotation'];
  onOpen: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Text variant="title">{label}</Text>
        <Button size="sm" variant="outline" onClick={onOpen}>Open</Button>
      </div>
      <DeviceFrame device={deviceFrame} theme={theme} className="mx-0">
        <StaticListingPreview variant={variant} qualification={qualification} />
      </DeviceFrame>
      <NudgeAnnotationCard title={label} annotation={annotation} />
    </div>
  );
}
